import { useTranslation } from "@/hooks";
import { Alert, Button, Container, Stack, Text, Title } from "@mantine/core";
import * as Sentry from "@sentry/react";
import type { ReactNode } from "react";

function ErrorFallback({
  error,
  resetError,
}: {
  error: unknown;
  resetError: () => void;
}) {
  const { t } = useTranslation();
  const errorMessage = error instanceof Error ? error.message : String(error);
  const isDev = import.meta.env.DEV;
  return (
    <Container size="md" py="xl">
      <Stack gap="md">
        <Title order={2}>{t("error").somethingWentWrong}</Title>
        <Alert color="red" title={t("error").applicationError}>
          <Text>
            {t("error").unexpectedErrorReported}
          </Text>
          {isDev && (
            <Text mt="sm" size="sm" c="gray.9">
              {t("common").error}: {errorMessage}
            </Text>
          )}
        </Alert>
        <Button onClick={resetError} variant="light">
          {t("error").tryAgain}
        </Button>
        <Button
          onClick={() => {
            window.location.href = "/";
          }}
          variant="subtle"
        >
          {t("error").goToHomePage}
        </Button>
      </Stack>
    </Container>
  );
}

/**
 * Error Boundary component using Sentry's withErrorBoundary HOC
 * Automatically captures React component errors and displays a fallback UI
 */
export const AppErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: ReactNode }) => <>{children}</>,
  {
    fallback: ({ error, resetError }) => (
      <ErrorFallback error={error} resetError={resetError} />
    ),
    showDialog: false,
  }
);
